import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "../../data/queries/userPreferencesQueries";
import { useUpdateUserPreferences } from "../../data/mutations/userPreferencesMutations";
import { toast } from "react-toastify";
import { GeneralNotificationsContent } from "./NotificationPreferencesComponents/GeneralNotificationsContent";
import { AssigneeNotificationsContent } from "./NotificationPreferencesComponents/AssigneeNotificationsContent";
import { CollaboratorNotificationsContent } from "./NotificationPreferencesComponents/CollaboratorNotificationsContent";
import { useQueryClient } from "@tanstack/react-query";
import { PreferredEmailLangContent } from "./NotificationPreferencesComponents/PreferredEmailLangContent";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import Enums from "../../types/enums";

export const NotificationPreferencesContent = () => {
  const { t } = useTranslation(["Preferences"]);
  const { UserId } = useDueDatesAuth();
  const queryClient = useQueryClient();

  const preferredEmailsLangMsgRef = useRef(null);
  const tasksSummaryEmailEnabledRef = useRef(null);
  const taskDueSoonEmailEnabledRef = useRef(null);
  const addedToTaskBoardEmailEnabledRef = useRef(null);
  const tasksAssignedToMeEmailEnabledRef = useRef(null);
  const newCommentsAddedEmailEnabledRef = useRef(null);
  const addedAsCollaboratorEmailEnabledRef = useRef(null);

  const { data: notificationPreferences, isPending } =
    useUserPreferences(UserId);
  const { mutate: updateUserPreferences } = useUpdateUserPreferences();

  const handleNotificationPreferencesChange = (e) => {
    let emailFrequency = notificationPreferences?.EmailFrequency;
    if (e.value == "emailFrequencyWeekly") {
      emailFrequency = Enums.EmailFrequency.Weekly;
    } else if (e.value == "emailFrequencyMonthly") {
      emailFrequency = Enums.EmailFrequency.Monthly;
    }

    let notifyMeAbout = notificationPreferences?.NotifyMeAbout;
    if (e.value == "notifyMeAboutAllTasks") {
      notifyMeAbout = Enums.NotifyMeAbout.AllTasks;
    } else if (e.value == "notifyMeAboutMyTasks") {
      notifyMeAbout = Enums.NotifyMeAbout.MyTasks;
    } else if (e.value == "notifyMeAboutTasksRelatedToMe") {
      notifyMeAbout = Enums.NotifyMeAbout.TasksRelatedToMe;
    }

    const userPreferences = {
      UserId: UserId,
      PreferredEmailLanguage: preferredEmailsLangMsgRef.current?.getValue(),
      TaskSummaryEmailEnabled: tasksSummaryEmailEnabledRef.current?.checked,
      EmailFrequency: emailFrequency,
      NotifyMeAbout: notifyMeAbout,
      TaskDueSoonEmailEnabled: taskDueSoonEmailEnabledRef.current?.checked,
      AddedToTaskBoardEmailEnabled:
        addedToTaskBoardEmailEnabledRef.current?.checked,
      TaskAssignedToMeEmailEnabled:
        tasksAssignedToMeEmailEnabledRef.current?.checked,
      NewCommentsEmailEnabled: newCommentsAddedEmailEnabledRef.current?.checked,
      AddedAsCollaboratorEmailEnabled:
        addedAsCollaboratorEmailEnabledRef.current?.checked,
      // UpdatesToTaskStageEmailEnabled:
      //   updatesToStageEmailEnabledRef.current.checked,
    };

    updateUserPreferences(
      {
        userPreferences: userPreferences,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["get-user-preferences", UserId]);
          toast.success(t("NotificationPreferences.Updated"), {
            position: "bottom-left",
          });
        },
        onError: () => {
          toast.error(t("NotificationPreferences.Error"), {
            position: "bottom-left",
          });
        },
      }
    );
  };

  if (isPending) return null;

  return (
    <div className="notifications-content">
      {/* Preferred Email Language */}
      <PreferredEmailLangContent
        preferredEmailsLangMsgRef={preferredEmailsLangMsgRef}
        notificationPreferences={notificationPreferences}
        onChange={handleNotificationPreferencesChange}
      />

      <hr />
      {/* General Notifications */}
      <GeneralNotificationsContent
        tasksSummaryEmailEnabledRef={tasksSummaryEmailEnabledRef}
        taskDueSoonEmailEnabledRef={taskDueSoonEmailEnabledRef}
        addedToTaskBoardEmailEnabledRef={addedToTaskBoardEmailEnabledRef}
        newCommentsAddedEmailEnabledRef={newCommentsAddedEmailEnabledRef}
        onChange={handleNotificationPreferencesChange}
        userNotificationData={notificationPreferences}
      />

      <hr />
      {/* Assignee Notifications */}
      <AssigneeNotificationsContent
        tasksAssignedToMeEmailEnabledRef={tasksAssignedToMeEmailEnabledRef}
        onChange={handleNotificationPreferencesChange}
        userNotificationData={notificationPreferences}
      />

      <hr />
      {/* Collaborators Notifications */}
      <CollaboratorNotificationsContent
        addedAsCollaboratorEmailEnabledRef={addedAsCollaboratorEmailEnabledRef}
        //updatesToStageEmailEnabledRef={updatesToStageEmailEnabledRef}
        onChange={handleNotificationPreferencesChange}
        userNotificationData={notificationPreferences}
      />
    </div>
  );
};
